import { VisitOptions } from '@inertiajs/core/types/types';
import { router } from '@inertiajs/react';
import classNames from 'classnames';
import * as React from 'react';
import { AnchorHTMLAttributes, MouseEventHandler, forwardRef } from 'react';

import { useSession } from '../SessionContext/SessionContext';

import styles from './CleanLinkButton.module.css';

type CleanLinkButtonProps = {
    fullWidth?: boolean;
    fullHeight?: boolean;
    disableInertia?: boolean;
    disabled?: boolean;
    align?: 'left' | 'center' | 'right';
    method?: 'get' | 'post';
    visitOptions?: VisitOptions;
    preventDrag?: boolean;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onDrag'>;

const CleanLinkButton = forwardRef<HTMLAnchorElement, CleanLinkButtonProps>(
    (
        {
            className,
            href = '#',
            fullWidth = false,
            fullHeight = false,
            disableInertia = false,
            preventDrag = false,
            visitOptions = {},
            align = 'center',
            method,
            disabled,
            ...anchorProps
        },
        ref
    ) => {
        const session = useSession();
        const classes = classNames(className, styles.Root, {
            [styles.fullWidth]: fullWidth,
            [styles.fullHeight]: fullHeight,
            [styles.disabled]: disabled,
            [styles.alignLeft]: align === 'left',
            [styles.alignCenter]: align === 'center',
            [styles.alignRight]: align === 'right',
        });

        const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
            if (disabled) {
                event.preventDefault();
                return;
            }

            anchorProps.onClick && anchorProps.onClick(event);

            if (!disableInertia && !event.metaKey && !event.defaultPrevented) {
                event.preventDefault();

                const { data, ...otherVisitOptions } = visitOptions;
                const sessionData =
                    method === 'get' ? {} : { tenantId: session.tenantId };

                router.visit(href, {
                    method: method,
                    data: { ...sessionData, ...(data ?? {}) },
                    ...otherVisitOptions,
                });
            }
        };

        return (
            <a
                {...anchorProps}
                ref={ref}
                href={href}
                className={classes}
                onClick={handleClick}
                draggable={preventDrag ? false : undefined}
            />
        );
    }
);

export default CleanLinkButton;
